import truncate from 'lodash/truncate';

import { getCode } from './utils';
import {
  BarcodeDisplayOptions,
  BarcodeTypes,
  isLabelSize,
  LabelContent,
  LabelData,
  LabelItemMatrix,
  LabelMargins,
  LabelSize,
} from './utils/types';
import { getVendor } from './utils/vendorIdHelpers';

export const getZpl = (options: ConstructorParameters<typeof LabelPrintTemplate>[0]) =>
  new LabelPrintTemplate(options).zplTemplate;

/**
 * Class LabelPrintTemplate handles the generation of ZPL templates
 * using the Label API data
 */
export default class LabelPrintTemplate {
  headerCommands: string;
  labelSize: LabelSize;
  labelData: LabelData;
  labelQuantity: number;
  labelMargins: LabelMargins;
  zplTemplate: string;
  displayPrice?: boolean;
  barcodeDisplayOption?: BarcodeDisplayOptions;
  barcodeType?: BarcodeTypes;
  labelContent?: LabelContent;
  dateNow?: string;

  /**
   * Construct a ZPL template to be sent to the Zebra printer
   * @param {Boolean} setHeaderCommands - Set the zpl header variables
   * @param {String} labelSize - Selected media size (normal, small, alt, jewelry)
   * @param {Object} labelData - Label API data
   * @param {Number} labelQuantity - Label quantity to be printed
   * @param {Object} labelMargins - Margin adjustment up to +/- 20 (top and left)
   * @param {Boolean} displayPrice - Item price to display on label
   * @param {String} barcodeDisplayOption - Selected barcode option (barcode and numbers, barcode only, numbers only)
   * @param {String} barcodeType - Selected barcode type (system ID, UPC, EAN, Custom SKU, Manufacturer SKU)
   * @param {String} labelContent - Selected label content (none, MSRP, timestamps..)
   * @param {String} dateNow - Date of when the label was printed
   */

  constructor({
    setHeaderCommands = true,
    labelSize,
    labelData,
    labelQuantity,
    labelMargins,
    displayPrice = true,
    barcodeDisplayOption = BarcodeDisplayOptions.BARCODE_AND_NUMBERS,
    barcodeType = BarcodeTypes.SYSTEM_SKU,
    labelContent,
    dateNow,
  }: {
    setHeaderCommands: boolean;
    labelSize: LabelSize;
    labelData: LabelData;
    labelQuantity: number;
    labelMargins: LabelMargins;
    displayPrice?: boolean;
    barcodeDisplayOption?: BarcodeDisplayOptions;
    barcodeType?: BarcodeTypes;
    labelContent?: LabelContent;
    dateNow?: string;
  }) {
    if (!isLabelSize(labelSize) || typeof labelData !== 'object' || isNaN(labelQuantity)) {
      // eslint-disable-next-line no-throw-literal
      throw 'Invalid LabelPrintTemplate params';
    }

    this.zplTemplate = '';
    this.headerCommands = '';
    this.labelSize = labelSize;
    this.labelData = labelData;
    this.labelQuantity = labelQuantity;
    this.labelMargins = labelMargins;
    this.displayPrice = displayPrice;
    this.barcodeDisplayOption = barcodeDisplayOption;
    this.barcodeType = barcodeType;
    this.labelContent = labelContent;
    this.dateNow = dateNow;

    // eslint-disable-next-line default-case
    switch (labelSize) {
      case 'normal':
        this.zplTemplate = this.buildNormalTemplate();
        break;
      case 'small':
        this.zplTemplate = this.buildSmallTemplate();
        break;
      case 'alt':
        this.zplTemplate = this.buildAltTemplate();
        break;
      case 'jewelry':
        this.zplTemplate = this.buildJewelryTemplate();
        break;
    }

    if (setHeaderCommands) {
      // The ending space is _very_ important for printing single items
      this.headerCommands = `! U1 setvar "print.tone" "20.0" `;
    }

    this.zplTemplate =
      this.headerCommands +
      this.zplTemplate
        // remove spaces in front of zpl commands
        .replace(/\s+\^/g, '^')
        // remove comments
        .replace(/\^FX[^^]+/g, '')
        .trim();
  }

  /**
   * Return the integer amount from the item's price (no decimal)
   * @param {String} amount - The item's original amount from the Label API without currency symbol,
   *                          ex: "12.42" or "12" if there is no decimal
   * @returns {String}
   */
  formatDollars(amount: string) {
    return String(Math.abs(Math.trunc(parseFloat(amount))));
  }

  /**
   * Return the decimal amount from the item's price
   * @param {String} amount - The item's original amount from the Label API without currency symbol,
   *                          ex: "12.42" or "12" if there is no decimal
   * @returns {String}
   */
  formatCents(amount: string) {
    return parseFloat(amount).toFixed(2).split('.')[1];
  }

  /**
   * Truncates the name if over a certain characters.
   * Returns an empty zpl string if shop has no name.
   *
   * @return {{zpl: String}}
   */
  getShopNameInfo({
    value = this.labelData.m.title,
    size = 30,
    width = 465,
    top = 15,
    truncateAt = null,
  }) {
    if (value === 'none') {
      return { zpl: '' };
    }
    const truncateLength = truncateAt || Math.ceil(width / (size / 2));
    const name = truncate(value, { length: truncateLength });
    return {
      zpl: `
        ^FX Black Bar with shop name
        ^CF0,${size}
        ^FO0,0
        ^GB${width},50,25^FS
        ^FO0,${top}^FR^FB${width},1,,C${this.cleanFieldData(name)}^FS
      `,
    };
  }

  /**
   * Min character is 1, max is 3. Width is fixed, font size is
   * proportional to number of characters.
   *
   * @return {{width: Number, zpl: String}}
   */
  getCurrencyInfo({
    value = this.labelData.m.currency_symbol || '$',
    left = 0,
    top = 0,
    width = 30,
    fontSize = 44,
    offsetTop = 2,
  } = {}) {
    const size = (fontSize / value.length) * (value.length > 1 ? 1.2 : 1);
    return {
      width,
      zpl: `
        ^FX currency symbol
        ^CF0,${size},${size}
        ^FO${left},${top + offsetTop}
        ^FB${width},1,,C
        ^FD${value}
        ^FS
      `,
    };
  }

  /**
   * Characters are monospaced, so we can be confident about the width we
   * set based on the length of the value. If the width is over the maxWidth
   * option, we'll resize the size on a proportion based on the extra
   * characters length.
   *
   * @return {{left: Number, width: Number, zpl: String}}
   */
  getDollarAmountInfo({
    value = this.formatDollars(this.labelData.m.price),
    left = 0,
    top = 0,
    size = 80,
    maxWidth = 150,
    minWidth = 45,
    negativeAmountWidth = 25,
    lineGraphicBox = '20,5,3',
  } = {}) {
    const isNegativeAmount = parseFloat(this.labelData.m.price) < 0;
    if (!isNegativeAmount) {
      // eslint-disable-next-line no-param-reassign
      negativeAmountWidth = 0;
    }
    const { newSize, width } = this.getDollarWidth({
      length: value.length,
      size,
      maxWidth,
      minWidth,
      negativeAmountWidth,
    });

    return {
      left,
      width,
      zpl: `
        ^FX dollar amount
        ${
          isNegativeAmount
            ? `^FO${left + 3},${top + newSize / 3}
            ^GB${lineGraphicBox}
            ^FS`
            : ''
        }
        ^CF0,${newSize},${newSize}
        ^FO${left + negativeAmountWidth},${top}
        ^FD${value}
        ^FS
      `,
    };
  }

  /**
   * Calculates dollar width based on length, size, maxWidth and minWidth
   * When width is larger then maxWidth, set size based on number length
   * (twice the size to match our sizeCalc)
   *
   * @return {Object}
   */
  getDollarWidth({
    length,
    size,
    maxWidth,
    minWidth,
    negativeAmountWidth = 0,
  }: {
    length: number;
    size: number;
    maxWidth: number;
    minWidth: number;
    negativeAmountWidth?: number;
  }) {
    const sizeCalc = size / 2;
    let width = length * sizeCalc + negativeAmountWidth;
    let newSize = size;
    if (width > maxWidth) {
      newSize = ((maxWidth - negativeAmountWidth) / length) * 2;
      width = maxWidth;
    } else if (width < minWidth) {
      width = minWidth;
    }
    return { newSize, width };
  }

  /**
   * Cents always have 2 characters with an underline below it.
   *
   * @return {{left: Number, width: Number, zpl: String}}
   */
  getCentsInfo({
    value = this.formatCents(this.labelData.m.price),
    left = 0,
    top = 0,
    size = 30,
    offsetTop = 28,
    width = 45,
    lineGraphicBox = '28,5,3',
  } = {}) {
    return {
      left,
      width,
      zpl: `
        ^FX cent
        ^CF0,${size},${size}
        ^FO${left},${top}^FD${value}
        ^FS
        ^FX underline cent
        ^FO${left},${top + offsetTop}
        ^GB${lineGraphicBox}
        ^FS
      `,
    };
  }

  getItemField(field: string) {
    return field in this.labelData.Item;
  }

  getLabelContent({ ...args }: Parameters) {
    let content = { zpl: '' };

    // eslint-disable-next-line default-case
    switch (this.labelContent) {
      case 'msrp':
        LabelPrintTemplate.printMsrp(this.labelData) &&
          (content = this.getLabelContentInfo({
            ...args,
            value: LabelPrintTemplate.getMsrp(this.labelData)?.amount,
            description: 'MSRP',
          }));
        break;
      case 'Note':
        this.labelData.Item.Note?.note &&
          (content = this.getLabelContentInfo({
            ...args,
            value: this.labelData.Item.Note.note,
            currencySymbol: null,
          }));
        break;
      case 'customSku':
        this.labelData.Item.customSku &&
          (content = this.getLabelContentInfo({
            ...args,
            value: this.labelData.Item.customSku,
            currencySymbol: null,
            description: 'SKU:',
          }));
        break;
      case 'manufacturerSku':
        content = this.getLabelContentInfo({
          ...args,
          value: this.labelData.Item.manufacturerSku,
          currencySymbol: null,
          description: 'SKU:',
        });
        break;
      case 'Category':
        this.getItemField('Category') &&
          (content = this.getLabelContentInfo({
            ...args,
            value: this.labelData.Item.Category?.name,
            currencySymbol: null,
          }));
        break;
      case 'Manufacturer':
        this.getItemField('Manufacturer') &&
          (content = this.getLabelContentInfo({
            ...args,
            value: this.labelData.Item.Manufacturer?.name,
            currencySymbol: null,
          }));
        break;
      case 'Vendor':
        content = this.getLabelContentInfo({
          ...args,
          value: getVendor(this.labelData),
          currencySymbol: null,
        });
        break;
      case 'printTimestamp':
        this.dateNow &&
          (content = this.getLabelContentInfo({
            ...args,
            value: this.dateNow,
            currencySymbol: null,
          }));
        break;
      case 'ItemMatrix':
        if (this.labelData.Item.ItemMatrix) {
          const itemMatrix = this.labelData.Item.ItemMatrix;
          let value: string = '';

          const attributeKeys = Object.keys(itemMatrix).filter((key) => key.includes('attribute'));

          attributeKeys.forEach((attribute) => {
            value += ` ${itemMatrix[attribute as keyof LabelItemMatrix]}`;
          });

          content = this.getLabelContentInfo({
            ...args,
            value,
            currencySymbol: null,
          });
        }
        break;
      case 'none':
        break;
    }

    return content;
  }

  // MSRP value is under labelData.m.msrp only if "Show MSRP" is checked in Settings > Locations > Customize > Settings

  public static getMsrp(data: LabelData) {
    return data.Item.Prices.ItemPrice.find((priceType) => priceType.useType === 'MSRP');
  }

  public static getDefaultPrice(data: LabelData) {
    return data.Item.Prices.ItemPrice.find((priceType) => priceType.useType === 'Default');
  }

  public static printMsrp(data: LabelData) {
    // NOTE: for MSRP - if the default price is less than MSRP, MSRP should be printed, however if the default is equal or higher than MSRP, MSRP should NOT be printed
    const msrp = LabelPrintTemplate.getMsrp(data)?.amount;
    const defaultPrice = LabelPrintTemplate.getDefaultPrice(data)?.amount;

    if (msrp && defaultPrice) {
      if (+defaultPrice >= +msrp) return false;

      if (+defaultPrice < +msrp) return true;
    }

    return false;
  }

  /**
   * Returns empty string if no value given
   * (when MSRP label printing is unchecked in Settings > Shop Setup > Labels)
   *
   * @return {{zpl: String}}
   */
  getLabelContentInfo({
    value = '',
    description = '',
    currencySymbol = this.labelData.m.currency_symbol,
    left = 0,
    top = 0,
    size = 20,
    offsetTop = 81,
    maxWidth,
  }: Parameters = {}) {
    // If no value, return empty zpl string
    if (!value) {
      return { zpl: '' };
    }

    let dollar;
    let cent;
    let price;

    if (currencySymbol) {
      dollar = this.formatDollars(value);
      cent = this.formatCents(value);
      price = `${currencySymbol}${dollar}.${cent}`;
    }

    return {
      zpl: `
        ^FX LABEL CONTENT
        ^CF0,${size},${size}
        ^FO${left},${top + offsetTop}
        ^FB${maxWidth},1,,L
        ^FD${description} ${currencySymbol ? price : value}
        ^FS
      `,
    };
  }

  /**
   * Use `labelWidth`, `marginLeft` and left position to calculate available
   * box width. Truncates the value if over a certain number of characters
   * we can add in the box depending on the width and lines available.
   *
   * @return {{zpl: String}}
   */
  getDescriptionInfo({
    value = this.labelData.Item.description,
    left = 0,
    top = 0,
    size = 25,
    lines = 4,
    labelWidth = 465,
    marginLeft = 10,
    maxWidth = null,
    truncateOffset = 10,
  }: Parameters = {}) {
    // Get available width based on labelWidth, left position and margins
    let width = labelWidth - left - marginLeft * 2;
    // If maxWidth is given, force width with value
    width = maxWidth || width;
    // Consider number of uppercase characters for truncateOffset
    const upperNum = value.length - value.replace(/[A-Z]/g, '').length;
    // eslint-disable-next-line no-param-reassign
    truncateOffset -= upperNum / 4;
    // Set maximum characters we can output in the description box, will be
    // used as a truncate value if the length of the value is longer
    const truncateLength = (width / (size / 2)) * lines + truncateOffset;
    const valueTruncated = truncate(value, { length: truncateLength });
    return {
      zpl: `
        ^FX Product name
        ^CF0,${size}
        ^FO${left},${top}
        ^FB${width},${lines + 1},,L,
        ${this.cleanFieldData(valueTruncated)}
        ^FS
        ^FO${left},${top - 1 + lines * size}
        ^GB${width},${size * 2},${size},W
        ^FS
      `,
    };
  }

  /**
   * Uses the hex codes to replace the tilde and caret characters https://www.zebra.com/us/en/support-downloads/knowledge-articles/ait/Print-Out-The-Tilde-Sign-By-Using-ZPL-commands.html
   */
  private cleanFieldData = (input: string): string => {
    if (!input.includes('^') && !input.includes('~')) {
      return `^FD${input}`;
    }
    const delimiter = this.getHexDelimiter(input);

    const replacer = this.getHexCode(delimiter);

    const cleanedInput = input
      .replace(/\^/g, replacer('^'))
      .replace(/~/g, replacer('~'))
      // The backslash character cannot be printed in ZPL https://www.zebra.com/content/dam/zebra/manuals/printers/common/programming/zpl-zbi2-pm-en.pdf page 1397. Replace with a forward slash
      .replace(/\\/g, '/');

    return `^FH${delimiter}^FD${cleanedInput}`;
  };

  private getHexDelimiter = (input: string, charCode = 34): string => {
    const delimiter = String.fromCharCode(charCode);
    if (!input.includes(delimiter)) {
      return delimiter;
    }
    return this.getHexDelimiter(input, charCode + 1);
  };

  private getHexCode =
    (delimiter: string) =>
    (target: string): string => {
      return `${delimiter}${target.charCodeAt(0).toString(16)}`;
    };

  /**
   * Can set width, height, and module width in options.
   *
   * @return {{zpl: String}}
   */

  getBarcodeInfo({
    value: originalValue = this.labelData.Item[this.barcodeType!],
    top = 220,
    left: originalLeft = 90,
    moduleWidth: originalModuleWidth = 3,
    labelWidth = 0,
    orientation = 'N',
    height = 40,
    size = 30,
    textJustification = 'C',
    lines = 1,
  }: Parameters = {}) {
    const value = originalValue === '' ? this.labelData.Item.systemSku : originalValue;
    const printNumbersOnly = this.barcodeDisplayOption === BarcodeDisplayOptions.NUMBERS;
    const printBarcodeAndNumbers =
      this.barcodeDisplayOption === BarcodeDisplayOptions.BARCODE_AND_NUMBERS;
    const isJewelryLabel = this.labelSize === LabelSize.JEWELRY;
    const isSmallLabel = this.labelSize === LabelSize.SMALL;
    let left = originalLeft;
    let moduleWidth = originalModuleWidth;

    const { code, char } = getCode(
      value,
      this.barcodeType || BarcodeTypes.SYSTEM_SKU,
      this.labelSize
    );

    if (!isJewelryLabel && !isSmallLabel && char === 'C') {
      left = 0;
      moduleWidth = value.length > 13 ? 1 : 2;
    }

    if (printNumbersOnly) {
      return {
        zpl: `
          ^FX Barcode
          ${isSmallLabel ? '^FWB,1' : ''}
          ^CF0,${size},${size}
          ^FO${left},${top}
          ${!isSmallLabel ? `^FB${labelWidth},${lines},,${textJustification}` : ''}
          ^FD${value}
          ^FS
        `,
      };
    }

    return {
      zpl: `
        ^FX Barcode
        ^FT${char === 'C' ? 25 : left},${top}
        ^BY${moduleWidth}
        ^B${char}${orientation},${height},${printBarcodeAndNumbers ? 'Y' : 'N'},N
        ^FD${code}
        ^FS
      `,
    };
  }

  /**
   * Build the Normal label size ZPL template
   * @returns {string}
   */
  buildNormalTemplate() {
    // Base template values
    const labelWidth = 465;
    const marginLeft = 10;
    let offsetTop = 60;
    let currencyInfo;
    let dollarAmountInfo;
    let centInfo;

    // No shop name? Rewrite offsetTop to make up for space left blank
    if (this.labelData.m.title === 'none') {
      offsetTop = 20;
    }

    // Get shop name information
    const shopNameInfo = this.getShopNameInfo({
      width: labelWidth,
    });

    if (this.displayPrice) {
      // Get currency symbol information
      currencyInfo = this.getCurrencyInfo({
        left: marginLeft,
        top: offsetTop,
      });

      // Get dollar amount information
      dollarAmountInfo = this.getDollarAmountInfo({
        left: marginLeft + currencyInfo.width,
        top: offsetTop,
      });

      // Get cent information
      centInfo = this.getCentsInfo({
        left: dollarAmountInfo.left + dollarAmountInfo.width,
        top: offsetTop,
      });
    }

    const labelContent = this.getLabelContent({
      left: marginLeft,
      top: offsetTop,
      priceWidth:
        (dollarAmountInfo ? dollarAmountInfo.left + dollarAmountInfo.width : 0) +
        (centInfo ? centInfo.width : 0),
      maxWidth: labelWidth,
    });

    // Get description information
    const descriptionInfo = this.getDescriptionInfo({
      left: centInfo ? centInfo.left + centInfo.width : 10,
      top: offsetTop,
      labelWidth,
      marginLeft,
    });

    // Get Bar Code information
    const barCodeInfo = this.getBarcodeInfo({
      left: this.barcodeDisplayOption === BarcodeDisplayOptions.NUMBERS ? 0 : undefined,
      labelWidth,
    });

    // Return ZPL string result
    return `
      ^XA
      ^CI28
      ^PW${labelWidth}
      ^LT${this.labelMargins[LabelSize.NORMAL as LabelSize].top}
      ^LS${this.labelMargins[LabelSize.NORMAL as LabelSize].left}
      ^LH0,0
      ^FWN,0
      ${shopNameInfo.zpl || ''}
      ${currencyInfo?.zpl || ''}
      ${dollarAmountInfo?.zpl || ''}
      ${centInfo?.zpl || ''}
      ${descriptionInfo.zpl}
      ${labelContent?.zpl || ''}
      ${barCodeInfo.zpl}
      ^PQ${this.labelQuantity}
      ^XZ
    `;
  }

  /**
   * Build the Small label size ZPL template
   * @returns {string}
   */
  buildSmallTemplate() {
    const offsetTop = 20;
    const labelWidth = 448;
    const barCodeVerticalWidth = 35;
    let currencyInfo;
    let dollarAmountInfo;
    let centInfo;

    if (this.displayPrice) {
      currencyInfo = this.getCurrencyInfo({
        top: offsetTop,
        width: 24,
        fontSize: 36,
      });

      dollarAmountInfo = this.getDollarAmountInfo({
        left: currencyInfo.width,
        top: offsetTop,
        size: 60,
        maxWidth: 125,
        minWidth: 35,
      });

      centInfo = this.getCentsInfo({
        left: dollarAmountInfo.left + dollarAmountInfo.width,
        top: offsetTop,
      });
    }

    const labelContent = this.getLabelContent({
      left: 5,
      top: this.displayPrice ? offsetTop : 30,
      size: 14,
      offsetTop: this.displayPrice ? 54 : 0,
      maxWidth: 170,
    });

    const isLabelContent = labelContent?.zpl !== '';
    let descriptionOffsetTop = 80;

    if (isLabelContent) descriptionOffsetTop = 95;
    if (isLabelContent && !this.displayPrice) descriptionOffsetTop = 50;
    if (!isLabelContent && !this.displayPrice) descriptionOffsetTop = 25;

    const descriptionInfo = this.getDescriptionInfo({
      left: 5,
      top: descriptionOffsetTop,
      lines: isLabelContent ? 4 : 5,
      size: 24,
      labelWidth,
      maxWidth: 170,
      truncateOffset: 5,
      marginLeft: 0,
    });

    const printNumbersOnly = this.barcodeDisplayOption === BarcodeDisplayOptions.NUMBERS;

    const barCodeInfo = this.getBarcodeInfo({
      top: printNumbersOnly ? 40 : 170,
      left: printNumbersOnly ? 235 : 215,
      moduleWidth: '2,4,6',
      orientation: 'B',
      height: barCodeVerticalWidth,
      size: 24,
      lines: printNumbersOnly ? 12 : 1,
    });

    return `
    ^XA
    ^CI28
    ^PW${labelWidth}
    ^LT${this.labelMargins[LabelSize.SMALL as LabelSize].top}
    ^LS${this.labelMargins[LabelSize.SMALL as LabelSize].left}
    ^LH105,0
    ^FWN,0
    ${currencyInfo?.zpl || ''}
    ${dollarAmountInfo?.zpl || ''}
    ${centInfo?.zpl || ''}
    ${descriptionInfo.zpl}
    ${labelContent?.zpl || ''}
    ${barCodeInfo.zpl}
    ^PQ${this.labelQuantity}
    ^XZ
  `;
  }

  /**
   * Build the Alt label size ZPL template
   * @returns {string}
   */
  buildAltTemplate() {
    let offsetTop = 60;
    const marginLeft = 15;
    const labelWidth = 448;
    let currencyInfo;
    let dollarAmountInfo;
    let centInfo;
    const isShowTitle = this.labelData.m.title !== 'none';

    if (!isShowTitle) offsetTop = 30;

    const shopNameInfo = this.getShopNameInfo({
      width: labelWidth,
      size: 28,
      top: 22,
    });

    if (this.displayPrice) {
      currencyInfo = this.getCurrencyInfo({
        left: marginLeft,
        top: offsetTop,
      });

      dollarAmountInfo = this.getDollarAmountInfo({
        left: marginLeft + currencyInfo.width,
        top: offsetTop,
        size: 70,
        maxWidth: 120,
      });

      centInfo = this.getCentsInfo({
        left: dollarAmountInfo.left + dollarAmountInfo.width,
        top: offsetTop,
      });
    }

    const labelContent = this.getLabelContent({
      left: 20,
      top: isShowTitle ? 35 : 10,
      size: 18,
      maxWidth: labelWidth,
    });

    const displayMSRP = this.labelContent === LabelContent.MSRP;
    const displayMsrpHiddenPrice = displayMSRP && !this.displayPrice;

    const descriptionInfo = this.getDescriptionInfo({
      left: centInfo ? centInfo.left + centInfo.width : 20,
      top: offsetTop,
      lines: displayMsrpHiddenPrice ? 1 : 3,
      size: displayMsrpHiddenPrice ? 20 : 24,
      truncateOffset: 0,
      labelWidth: displayMsrpHiddenPrice ? labelWidth + 25 : labelWidth,
      marginLeft: displayMsrpHiddenPrice ? 0 : marginLeft,
    });

    const barCodeInfo = this.getBarcodeInfo({
      top: 180,
      left: this.barcodeDisplayOption === BarcodeDisplayOptions.NUMBERS ? 0 : 120,
      moduleWidth: 2,
      labelWidth,
    });

    return `
      ^XA
      ^CI28
      ^PW${labelWidth}
      ^LT${this.labelMargins[LabelSize.ALT as LabelSize].top}
      ^LS${this.labelMargins[LabelSize.ALT as LabelSize].left}
      ^LH10,0
      ^FWN,0
      ${shopNameInfo.zpl}
      ${currencyInfo?.zpl || ''}
      ${dollarAmountInfo?.zpl || ''}
      ${centInfo?.zpl || ''}
      ${descriptionInfo.zpl}
      ${labelContent?.zpl || ''}
      ${barCodeInfo.zpl}
      ^PQ${this.labelQuantity}
      ^XZ
    `;
  }

  /**
   * Build the Jewelry label size ZPL template
   * @returns {string}
   */
  buildJewelryTemplate() {
    const labelWidth = 448;
    const centPosition = 420;
    const amountTopPosition = 50;
    let centInfo;
    let dollarAmountInfo;
    let currencyInfo;

    const barCodeInfo = this.getBarcodeInfo({
      top: 25,
      left:
        this.barcodeType === BarcodeTypes.UPC &&
        this.barcodeDisplayOption !== BarcodeDisplayOptions.NUMBERS
          ? 30
          : 15,
      moduleWidth: '2,2.0,10',
      height: 35,
      labelWidth,
      size: 18,
      textJustification: 'L',
    });

    const labelContent = this.getLabelContent({
      // MSRP will be left aligned with the start of the description
      left: 305,
      top: 35,
      offsetTop: 0,
      size: 12,
      maxWidth: 130,
    });

    const isLabelContent = labelContent?.zpl !== '';

    const descriptionInfo = this.getDescriptionInfo({
      left: 305,
      top: 0,
      lines: isLabelContent ? 2 : 3,
      size: 16,
      maxWidth: 130,
      truncateOffset: 5,
    });

    if (this.displayPrice) {
      centInfo = this.getCentsInfo({
        left: centPosition,
        top: amountTopPosition,
        size: 16,
        width: 45,
        offsetTop: 15,
        lineGraphicBox: '15,2,3',
      });

      const dollarValue = this.formatDollars(this.labelData.m.price);
      const dollarLength = dollarValue.length;
      const dollarOptions = {
        length: dollarLength,
        size: 30,
        maxWidth: 90,
        minWidth: 20,
      };

      const { width: dollarWidth } = this.getDollarWidth(dollarOptions);
      const negativeAmountWidth = parseFloat(this.labelData.m.price) < 0 ? 15 : 0;

      dollarAmountInfo = this.getDollarAmountInfo({
        left: centPosition - dollarWidth - negativeAmountWidth,
        top: amountTopPosition,
        size: dollarOptions.size,
        maxWidth: dollarOptions.maxWidth,
        minWidth: dollarOptions.minWidth,
        negativeAmountWidth,
        lineGraphicBox: '12,2,3',
      });

      const currencyWidth = 16;

      currencyInfo = this.getCurrencyInfo({
        left: centPosition - dollarWidth - negativeAmountWidth - currencyWidth,
        top: amountTopPosition,
        width: currencyWidth,
        fontSize: 20,
        offsetTop: 1,
      });
    }

    return `
      ^XA
      ^CI28
      ^MNM,5
      ^PW${labelWidth}
      ^LT${this.labelMargins[LabelSize.JEWELRY as LabelSize].top}
      ^LS${this.labelMargins[LabelSize.JEWELRY as LabelSize].left}
      ^LH0,0
      ^FWN,0
      ${barCodeInfo.zpl}
      ${descriptionInfo.zpl}
      ${currencyInfo?.zpl || ''}
      ${dollarAmountInfo?.zpl || ''}
      ${centInfo?.zpl || ''}
      ${labelContent?.zpl || ''}
      ^PQ${this.labelQuantity}
      ^XZ
    `;
  }
}

interface Parameters {
  value?: string;
  currencySymbol?: string | null;
  top?: number;
  left?: number;
  offsetTop?: number;
  moduleWidth?: number | string;
  chars?: number | string;
  orientation?: string;
  height?: number;
  size?: number;
  lines?: number;
  maxWidth?: number | null;
  truncateOffset?: number;
  labelWidth?: number;
  priceWidth?: number;
  marginLeft?: number;
  // L = left, C = center, R = right
  textJustification?: 'L' | 'C' | 'R';
  description?: string;
}
